import React, {useMemo, useState} from 'react';
import {graphql, HeadFC, PageProps} from 'gatsby';
import Button from 'design-system/src/Components/Button/Button';
import PageHead from '@src/components/general/PageHead/PageHead';
import {
  createDeepLink,
  getAppDeepLinkEntryUrl,
  getDeepLinkConfig,
  getRouteContract,
  normalizeDeepLinkPath,
  openDeepLink,
  openNativeAppDirectly,
} from '@src/utils/deepLinkUtils';
import {DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES} from '@src/Constant/languages';

type WellKnownResult = {
  body: string;
  contentType: string;
  status: number;
  url: string;
};

const cardStyle: React.CSSProperties = {
  background: '#16181d',
  border: '1px solid #2b2f36',
  borderRadius: 8,
  padding: 20,
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '12px 14px',
  borderRadius: 6,
  border: '1px solid #353a42',
  background: '#101216',
  color: '#ffffff',
  fontSize: 14,
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  color: '#c7cad1',
  display: 'block',
  fontSize: 13,
  marginBottom: 8,
};

const DeepLinkTestPage = (props: PageProps<Queries.DeepLinkTestQuery>) => {
  const initialLanguage =
    props.pageContext.language &&
    SUPPORTED_LANGUAGES.includes(props.pageContext.language as any)
      ? props.pageContext.language
      : DEFAULT_LANGUAGE;

  const [path, setPath] = useState('product/42');
  const [language, setLanguage] = useState(initialLanguage);
  const [inspection, setInspection] = useState<Record<string, WellKnownResult>>(
    {},
  );
  const [inspectionError, setInspectionError] = useState('');
  const [isInspecting, setIsInspecting] = useState(false);
  const [customFallback, setCustomFallback] = useState('');

  const normalizedPath = useMemo(() => normalizeDeepLinkPath(path), [path]);
  const deepLink = useMemo(
    () =>
      createDeepLink({
        path,
        language,
      }),
    [language, path],
  );
  const routeContract = useMemo(() => getRouteContract(path), [path]);
  const entryUrl = useMemo(
    () => getAppDeepLinkEntryUrl(path, language),
    [language, path],
  );

  const inspectFile = async (url: string) => {
    const response = await fetch(url);
    const body = await response.text();

    return {
      body,
      contentType: response.headers.get('content-type') || 'unknown',
      status: response.status,
      url,
    };
  };

  const inspectWellKnownFiles = async () => {
    setIsInspecting(true);
    setInspectionError('');

    try {
      const [aasa, assetLinks] = await Promise.all([
        inspectFile('/.well-known/apple-app-site-association'),
        inspectFile('/.well-known/assetlinks.json'),
      ]);

      setInspection({
        apple: aasa,
        android: assetLinks,
      });
    } catch (error) {
      setInspectionError('Unable to inspect .well-known files from this page.');
    } finally {
      setIsInspecting(false);
    }
  };

  const {
    androidPackageName,
    defaultAppPath,
    iosAppStoreId,
    routeMap,
    scheme,
    siteUrl,
  } = getDeepLinkConfig();

  return (
    <div
      style={{
        background: '#0b0d10',
        color: '#ffffff',
        minHeight: '100vh',
        padding: '32px 16px 48px',
      }}>
      <div
        style={{
          margin: '0 auto',
          maxWidth: 960,
          display: 'grid',
          gap: 20,
        }}>
        <div style={{display: 'grid', gap: 8}}>
          <h1 style={{fontSize: 28, margin: 0}}>Deep Link QA</h1>
          <p style={{color: '#b5bac3', lineHeight: 1.5, margin: 0}}>
            This page stays independent from CMS content so we can verify the
            full web-to-app contract in one place.
          </p>
        </div>

        <section style={cardStyle}>
          <div style={{display: 'grid', gap: 16}}>
            <div>
              <label htmlFor="deeplink-path" style={labelStyle}>
                App path
              </label>
              <input
                id="deeplink-path"
                value={path}
                onChange={event => setPath(event.target.value)}
                placeholder="product/42"
                style={inputStyle}
              />
            </div>

            <div>
              <label htmlFor="deeplink-language" style={labelStyle}>
                Language
              </label>
              <select
                id="deeplink-language"
                value={language}
                onChange={event => setLanguage(event.target.value)}
                style={inputStyle}>
                {SUPPORTED_LANGUAGES.map(item => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="deeplink-fallback" style={labelStyle}>
                Custom fallback URL
              </label>
              <input
                id="deeplink-fallback"
                value={customFallback}
                onChange={event => setCustomFallback(event.target.value)}
                placeholder="https://example.com/fallback"
                style={inputStyle}
              />
            </div>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 12,
              }}>
              <Button
                title="Open via shared helper"
                variant="contained"
                size="small"
                onPress={() =>
                  openDeepLink({
                    path,
                    language,
                    iosFallback: customFallback || undefined,
                    androidFallback: customFallback || undefined,
                  })
                }
              />
              <Button
                title="Open native scheme"
                variant="contained"
                size="small"
                onPress={() =>
                  openNativeAppDirectly(path, customFallback || undefined)
                }
              />
              <Button
                title="Open universal link"
                variant="contained"
                size="small"
                onPress={() => window.open(deepLink.universalUrl, '_self')}
              />
              <Button
                title="Open OneLink (AppsFlyer)"
                variant="contained"
                size="small"
                onPress={() =>
                  openDeepLink({
                    path,
                    language,
                    useOneLink: true,
                    attribution: {
                      pid: 'web',
                      campaign: 'deeplink_qa_page',
                    },
                    iosFallback: customFallback || undefined,
                    androidFallback: customFallback || undefined,
                  })
                }
              />
              <Button
                title={isInspecting ? 'Inspecting...' : 'Inspect .well-known'}
                variant="outlined"
                size="small"
                onPress={inspectWellKnownFiles}
                disabled={isInspecting}
              />
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <div style={{display: 'grid', gap: 12}}>
            <h2 style={{fontSize: 18, margin: 0}}>Generated values</h2>
            <div>
              <div style={labelStyle}>Normalized path</div>
              <code>{normalizedPath || '(empty)'}</code>
            </div>
            <div>
              <div style={labelStyle}>Entry route</div>
              <code>{entryUrl}</code>
            </div>
            <div>
              <div style={labelStyle}>Native URL</div>
              <code>{deepLink.nativeUrl}</code>
            </div>
            <div>
              <div style={labelStyle}>Universal URL</div>
              <code>{deepLink.universalUrl}</code>
            </div>
            <div>
              <div style={labelStyle}>Route contract URL</div>
              <code>{deepLink.routeContractUrl}</code>
            </div>
            <div>
              <div style={labelStyle}>OneLink (AppsFlyer)</div>
              <code style={{wordBreak: 'break-all'}}>
                {deepLink.oneLinkUrl}
              </code>
            </div>
            <div>
              <div style={labelStyle}>Share URL</div>
              <code style={{wordBreak: 'break-all'}}>{deepLink.shareUrl}</code>
            </div>
            <div>
              <div style={labelStyle}>Store fallback</div>
              <code>{deepLink.storeFallbackUrl}</code>
            </div>
            <div>
              <div style={labelStyle}>Route validation</div>
              <code>{`known=${routeContract.isKnownRoute}; requiresAuth=${routeContract.requiresAuth}; appPath=${routeContract.appPath}`}</code>
            </div>
          </div>
        </section>

        <section style={cardStyle}>
          <div style={{display: 'grid', gap: 12}}>
            <h2 style={{fontSize: 18, margin: 0}}>Current config</h2>
            <code>{`GATSBY_WEB_SITE_URL=${siteUrl}`}</code>
            <code>{`GATSBY_DEEP_LINK_SCHEME=${scheme}`}</code>
            <code>{`GATSBY_DEFAULT_DEEP_LINK_PATH=${defaultAppPath}`}</code>
            <code>{`GATSBY_APP_IOS_ID=${iosAppStoreId}`}</code>
            <code>{`GATSBY_ANDROID_PACKAGE=${androidPackageName}`}</code>
            <code>{`GATSBY_APPS_FLYER_SMART_BANNER_ENABLED=${process.env.GATSBY_APPS_FLYER_SMART_BANNER_ENABLED || ''}`}</code>
            <code>{`GATSBY_APPS_FLYER_ONELINK_URL=${process.env.GATSBY_APPS_FLYER_ONELINK_URL || '(empty — falls back to universal link)'}`}</code>
            <code>{`ROUTE_MAP=${Object.keys(routeMap).join(', ')}`}</code>
          </div>
        </section>

        <section style={cardStyle}>
          <div style={{display: 'grid', gap: 12}}>
            <h2 style={{fontSize: 18, margin: 0}}>.well-known inspection</h2>
            {inspectionError ? (
              <p style={{color: '#ff9ca3', margin: 0}}>{inspectionError}</p>
            ) : null}
            {(['apple', 'android'] as const).map(key => {
              const result = inspection[key];

              if (!result) {
                return null;
              }

              return (
                <div
                  key={key}
                  style={{
                    background: '#101216',
                    border: '1px solid #242932',
                    borderRadius: 6,
                    padding: 16,
                    display: 'grid',
                    gap: 8,
                  }}>
                  <strong>
                    {key === 'apple'
                      ? 'apple-app-site-association'
                      : 'assetlinks.json'}
                  </strong>
                  <code>{result.url}</code>
                  <code>{`status=${result.status}`}</code>
                  <code>{`content-type=${result.contentType}`}</code>
                  <pre
                    style={{
                      margin: 0,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: 12,
                      lineHeight: 1.5,
                    }}>
                    {result.body}
                  </pre>
                </div>
              );
            })}
          </div>
        </section>

        <section style={cardStyle}>
          <div style={{display: 'grid', gap: 12}}>
            <h2 style={{fontSize: 18, margin: 0}}>
              Staging verification checklist
            </h2>
            <ul
              style={{
                margin: 0,
                paddingLeft: 20,
                color: '#c7cad1',
                lineHeight: 1.6,
              }}>
              <li>
                Confirm <code>/.well-known/apple-app-site-association</code> and{' '}
                <code>/.well-known/assetlinks.json</code> are reachable from
                staging.
              </li>
              <li>
                Verify universal links open the app on iOS Safari and Android
                Chrome.
              </li>
              <li>
                Confirm promo-code URLs like <code>promocode?code=TEST123</code>
                preserve the code in the route contract and autofill on the web
                promo page.
              </li>
              <li>
                Confirm Android intent URI generation works for Android handoff
                and falls back cleanly when the app is missing.
              </li>
              <li>
                Confirm store fallback URLs contain deferred deep link referrer
                parameters.
              </li>
              <li>
                Validate <code>GATSBY_WEB_SITE_URL</code>,{' '}
                <code>GATSBY_DEEP_LINK_SCHEME</code>, and store IDs in staging
                env.
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>
  );
};

DeepLinkTestPage.layoutComponent = 'EmptyLayout';

export default DeepLinkTestPage;

export const Head: HeadFC<Queries.DeepLinkTestQuery> = props => (
  <PageHead
    title="Deep Link QA"
    description="Web to app deep link validation page"
    {...props}
  />
);

export const query = graphql`
  query DeepLinkTest($language: String!) {
    locales: allLocale(filter: {language: {eq: $language}}) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
  }
`;
